import React, { useContext, useState, useEffect, Fragment } from 'react';
import moment from 'moment';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import AddButton from 'components/v2/buttons/AddButton';
import ClipboardCopyIcon from 'components/v2/icons/ClipboardCopyIcon';
import SessionStore from 'stores/SessionStore';

import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

const CHARS_DISPLAYED = 4;
const maskKey = (key) => {
  return '*'
    .repeat(key.length - CHARS_DISPLAYED)
    .concat(key.slice(key.length - CHARS_DISPLAYED));
};

const APIKeys = (props) => {
  const [keys, setKeys] = useState([]);
  const [showModal, setModalVisibility] = useState(false);
  const [generatedKey, setGeneratedKey] = useState('');
  const { user, cardClassName } = props;
  const { t } = useTranslation();

  useEffect(() => {
    const getKeys = async () => {
      try {
        const result = await UserWebAPIUtils.getAPIKeys(user._id, (e) => {
          throw e;
        });
        if (result && result.success) {
          setKeys(
            result.data.items.map((item) => {
              return {
                ...item,
                maskedValue: maskKey(item.value),
              };
            }),
          );
        }
      } catch (e) {
        console.log(e);
      }
    };
    getKeys();
  }, []);

  const generateKey = async () => {
    const result = await UserWebAPIUtils.generateAPIKey(user._id).catch((e) => {
      console.log(e);
    });

    if (result && result.success) {
      setGeneratedKey(result.data.key);
      setModalVisibility(true);
      setKeys((currentKeys) => {
        return [
          ...currentKeys,
          { ...result.data, maskedValue: maskKey(result.data.value) },
        ];
      });
    }
  };

  const renderKeyList = () => {
    return keys.map((data) => {
      return (
        <Fragment key={data._id}>
          <div className="flex">
            <div className="w-full flex">
              <span className="w-40">{data.maskedValue}</span>
              <CopyToClipboard text={data.value}>
                <span className="cursor-pointer">
                  <ClipboardCopyIcon />
                </span>
              </CopyToClipboard>
            </div>
          </div>
          <span>{data.createdBy}</span>
          <span>{moment(data.dateCreated).format('L')}</span>
        </Fragment>
      );
    });
  };

  const resetGeneratedKey = () => {
    setGeneratedKey('');
    setModalVisibility(false);
  };

  const renderGeneratedKey = () => {
    return (
      <CargomaticModal
        title={t('GENERATED_KEY')}
        style={{ top: '30%' }}
        show={showModal}
        onHide={resetGeneratedKey}
        body={<div className="font-medium">{generatedKey}</div>}
        footer={() => {}}
      />
    );
  };

  const authorizedForAPIUse = () => {
    if (user._type === 'Shipper') {
      return user.can_pay || user.can_pay_manually;
    }
    if (user._type === 'Carrier') {
      return user.authorized;
    }
    return false;
  };

  const toolTipMessages = {
    shipper:
      'Only users with a registered method of payment can generate api keys',
    carrier: 'Only authorized users can generate api keys',
  };

  return (
    <CargomaticCard
      className={cardClassName}
      header={() => (
        <header className="flex items-center justify-between">
          <h3 className="my-0">{t('API_KEYS')}</h3>
          {authorizedForAPIUse() ? (
            <AddButton onClick={generateKey} />
          ) : (
            <OverlayTrigger
              placement="right"
              trigger="click"
              overlay={
                <Tooltip style={{ zIndex: 99999 }} id="can_pay_id">
                  {SessionStore.isShipper()
                    ? toolTipMessages.shipper
                    : toolTipMessages.carrier}
                </Tooltip>
              }
              children={
                SessionStore.isShipper()
                  ? toolTipMessages.shipper
                  : toolTipMessages.carrier
              }
            >
              <AddButton />
            </OverlayTrigger>
          )}
        </header>
      )}
    >
      <div className="grid grid-cols-3 gap-4">
        <span className="font-medium">{titleize(t('KEY'))}</span>
        <span className="font-medium">{t('CREATED_BY')}</span>
        <span className="font-medium">{t('DATE_CREATED')}</span>
        {renderKeyList()}
      </div>
      {generatedKey && renderGeneratedKey()}
    </CargomaticCard>
  );
};

export default APIKeys;
